import {
  EAutoCapability,
  EAutoCapabilityOverrideAction,
  EMessageRole,
  EReasoningMode,
  ESearchMode,
  type IAutoCapabilityOverride,
  type IReasoningModeOption,
} from "../types/chat";
import {
  EAutoOverrideOptionKey,
  type IAutoOverrideOption,
  type IAutoSearchModeOption,
  type IAutoTurnSummary,
  type IBuildComposerDisableOverrideProps,
  type IBuildRerunOverrideProps,
  type IComposerModes,
  type IFindLatestAutoAnswerProps,
  type IGetAutoSearchModeOptionsProps,
  type IGetReasoningModeOptionsProps,
  type INormalizeComposerModesProps,
  type IResolveComposerModesForModelChangeProps,
  type IResolveSearchModeAfterSendProps,
  type TAutoTurnSummaryLabelKey,
  type TAutoTurnSummaryMessage,
} from "./auto-composer.types";
import { isSearchModeAllowedByOrg } from "./search-mode-org-access";

// NOTE: NSAI-6541 — the composer and answer rules for Auto (the router model),
// shared so web and mobile behave the same. While Auto is selected, search and
// reasoning default to "Auto decides" (ESearchMode.AUTO / EReasoningMode.AUTO,
// contract C5); a manual pick is explicit and stays for the chat until the user
// changes it or picks another model.

const MANUAL_REASONING_MODE_OPTIONS: readonly IReasoningModeOption[] = [
  { mode: EReasoningMode.THINKING, labelKey: "input.reasoning_thinking" },
  { mode: EReasoningMode.FAST, labelKey: "input.reasoning_fast" },
];

const AUTO_REASONING_MODE_OPTIONS: readonly IReasoningModeOption[] = [
  { mode: EReasoningMode.AUTO, labelKey: "autoModel.composer.auto" },
  ...MANUAL_REASONING_MODE_OPTIONS,
];

// NOTE: The capabilities the answer line names, in display order: search and
// research, then the generators (the ticket's "model, search / research,
// image / doc / deck / page, reasoning"). Knowledge retrieval, file analysis and
// connectors are left out: retrieval fails open to "on" and attachments are
// always read, so naming them would put the line under almost every answer.
const SUMMARY_CAPABILITIES: readonly EAutoCapability[] = [
  EAutoCapability.WEB_SEARCH,
  EAutoCapability.DEEP_RESEARCH,
  EAutoCapability.IMAGE_GENERATION,
  EAutoCapability.DOCUMENT_GENERATION,
  EAutoCapability.DECK,
  EAutoCapability.HTML_PAGE,
];

// NOTE: The capabilities a one-click re-run can drop. Each maps to a composer
// control, so the choice can stick for the chat as an explicit value; the
// generators have no composer control and are named on the line only.
const SEARCH_CAPABILITY_OVERRIDES: ReadonlyArray<{
  capability: EAutoCapability;
  key: EAutoOverrideOptionKey;
}> = [
  {
    capability: EAutoCapability.WEB_SEARCH,
    key: EAutoOverrideOptionKey.WITHOUT_WEB_SEARCH,
  },
  {
    capability: EAutoCapability.DEEP_RESEARCH,
    key: EAutoOverrideOptionKey.WITHOUT_DEEP_RESEARCH,
  },
];

const SEARCH_CAPABILITIES: readonly EAutoCapability[] =
  SEARCH_CAPABILITY_OVERRIDES.map((entry) => entry.capability);

export const getReasoningModeOptions = ({
  isAutoSelected,
}: IGetReasoningModeOptionsProps): readonly IReasoningModeOption[] =>
  isAutoSelected ? AUTO_REASONING_MODE_OPTIONS : MANUAL_REASONING_MODE_OPTIONS;

// NOTE: The search selector shown while Auto is selected. Web search and deep
// research are listed only when the organization allows them (the same gate
// as the "+" menu); Auto can never switch on what the user could not.
export const getAutoSearchModeOptions = ({
  isWebSearchEnabled,
  isDeepResearchEnabled,
}: IGetAutoSearchModeOptionsProps): IAutoSearchModeOption[] => [
  { mode: ESearchMode.AUTO, labelKey: "autoModel.composer.auto" },
  ...(isWebSearchEnabled
    ? [{ mode: ESearchMode.WEB, labelKey: "input.web_search" as const }]
    : []),
  ...(isSearchModeAllowedByOrg({
    searchMode: ESearchMode.DEEP,
    isWebSearchEnabled,
    isDeepResearchEnabled,
  })
    ? [{ mode: ESearchMode.DEEP, labelKey: "input.deep_research" as const }]
    : []),
  { mode: ESearchMode.NONE, labelKey: "autoModel.composer.off" },
];

// NOTE: A user-made model change. Picking Auto hands search and reasoning to
// Auto; leaving it turns "Auto decides" back into today's defaults (search off,
// fast) and keeps any explicit pick. Hydrating a chat's saved selection is not
// a model change and must not call this, or it would wipe the chat's explicit
// choices on every visit.
export const resolveComposerModesForModelChange = ({
  wasAutoSelected,
  isAutoSelected,
  searchMode,
  reasoningMode,
}: IResolveComposerModesForModelChangeProps): IComposerModes => {
  if (isAutoSelected && !wasAutoSelected) {
    return { searchMode: ESearchMode.AUTO, reasoningMode: EReasoningMode.AUTO };
  }
  return normalizeComposerModes({ isAutoSelected, searchMode, reasoningMode });
};

// NOTE: "Auto decides" only exists while Auto is selected. Applied to what is
// sent, so a stale AUTO never reaches the server on an explicit model.
export const normalizeComposerModes = ({
  isAutoSelected,
  searchMode,
  reasoningMode,
}: INormalizeComposerModesProps): IComposerModes => {
  if (isAutoSelected) {
    return { searchMode, reasoningMode };
  }
  return {
    searchMode: searchMode === ESearchMode.AUTO ? ESearchMode.NONE : searchMode,
    reasoningMode:
      reasoningMode === EReasoningMode.AUTO
        ? EReasoningMode.FAST
        : reasoningMode,
  };
};

// NOTE: An explicit model's search toggle is per message, so it resets after a
// send. On Auto the search state is the chat's choice (Auto, or the explicit
// mode the user picked), so it stays.
export const resolveSearchModeAfterSend = ({
  isAutoSelected,
  sentSearchMode,
}: IResolveSearchModeAfterSendProps): ESearchMode =>
  isAutoSelected ? sentSearchMode : ESearchMode.NONE;

const getEnabledCapabilities = (
  message: TAutoTurnSummaryMessage,
): EAutoCapability[] => {
  if (message.role !== EMessageRole.ASSISTANT || !message.isAutoRouted) {
    return [];
  }
  return message.autoCapabilities ?? [];
};

// NOTE: What Auto switched on for one answer, or null when it switched on
// nothing the line names. Then only the NSAI-4948 badge (the model) renders.
export const getAutoTurnSummary = (
  message: TAutoTurnSummaryMessage,
): IAutoTurnSummary | null => {
  if (message.role !== EMessageRole.ASSISTANT || !message.isAutoRouted) {
    return null;
  }
  const enabled = getEnabledCapabilities(message);
  const capabilities = SUMMARY_CAPABILITIES.filter((capability) =>
    enabled.includes(capability),
  );
  const isThinking = message.autoReasoningMode === EReasoningMode.THINKING;
  if (capabilities.length === 0 && !isThinking) {
    return null;
  }
  const labelKeys: TAutoTurnSummaryLabelKey[] = [
    ...capabilities.map(
      (capability) => `autoModel.summary.capabilities.${capability}` as const,
    ),
    ...(isThinking ? ["autoModel.summary.thinking" as const] : []),
  ];
  return { capabilities, isThinking, labelKeys };
};

// NOTE: The one-click overrides offered under an Auto answer: drop a search
// capability Auto switched on, flip the reasoning mode Auto chose, or pick a
// model. Each re-runs the turn with the choice made explicit for the chat.
export const getAutoOverrideOptions = (
  message: TAutoTurnSummaryMessage,
): IAutoOverrideOption[] => {
  if (message.role !== EMessageRole.ASSISTANT || !message.isAutoRouted) {
    return [];
  }
  const enabled = getEnabledCapabilities(message);
  const searchOptions: IAutoOverrideOption[] =
    SEARCH_CAPABILITY_OVERRIDES.filter((entry) =>
      enabled.includes(entry.capability),
    ).map((entry) => ({
      key: entry.key,
      labelKey: `autoModel.override.options.${entry.key}` as const,
      searchMode: ESearchMode.NONE,
      // NOTE: Dropping one search capability drops search for the turn: the
      // composer has a single search mode, and Off is its explicit value.
      withoutCapabilities: enabled.filter((capability) =>
        SEARCH_CAPABILITIES.includes(capability),
      ),
      isModelChange: false,
    }));
  const isThinking = message.autoReasoningMode === EReasoningMode.THINKING;
  const reasoningKey = isThinking
    ? EAutoOverrideOptionKey.WITHOUT_THINKING
    : EAutoOverrideOptionKey.WITH_THINKING;
  return [
    ...searchOptions,
    {
      key: reasoningKey,
      labelKey: `autoModel.override.options.${reasoningKey}`,
      reasoningMode: isThinking ? EReasoningMode.FAST : EReasoningMode.THINKING,
      withoutCapabilities: [],
      isModelChange: false,
    },
    {
      key: EAutoOverrideOptionKey.WITH_OTHER_MODEL,
      labelKey: `autoModel.override.options.${EAutoOverrideOptionKey.WITH_OTHER_MODEL}`,
      withoutCapabilities: [],
      isModelChange: true,
    },
  ];
};

// NOTE: The override record a re-run carries, or null when it drops no
// capability Auto enabled (a reasoning or model change is not a capability
// override, NSAI-6544).
export const buildRerunOverride = ({
  messageUuid,
  option,
}: IBuildRerunOverrideProps): IAutoCapabilityOverride | null =>
  option.withoutCapabilities.length > 0
    ? {
        messageUuid,
        capabilities: option.withoutCapabilities,
        action: EAutoCapabilityOverrideAction.RERUN_WITHOUT,
      }
    : null;

// NOTE: Switching search Off in the composer, after a turn sent in "Auto
// decides" whose answer had Auto switch search on, overrides that decision.
// Counted once, on the send that carries the change.
export const buildComposerDisableOverride = ({
  isAutoSelected,
  lastSentSearchMode,
  searchModeForSend,
  latestAutoAnswer,
}: IBuildComposerDisableOverrideProps): IAutoCapabilityOverride | null => {
  if (
    !isAutoSelected ||
    !latestAutoAnswer ||
    lastSentSearchMode !== ESearchMode.AUTO ||
    searchModeForSend !== ESearchMode.NONE
  ) {
    return null;
  }
  const capabilities = getEnabledCapabilities(latestAutoAnswer).filter(
    (capability) => SEARCH_CAPABILITIES.includes(capability),
  );
  return capabilities.length > 0
    ? {
        messageUuid: latestAutoAnswer.uuid,
        capabilities,
        action: EAutoCapabilityOverrideAction.DISABLED,
      }
    : null;
};

// NOTE: The chat's latest assistant answer when Auto routed it, else null.
// Overrides are offered on that answer only: a re-run appends a new turn, so
// re-running an older one would answer out of order.
export const findLatestAutoAnswer = <TMessage extends TAutoTurnSummaryMessage>({
  messages,
}: IFindLatestAutoAnswerProps<TMessage>): TMessage | null => {
  const latestAnswer = [...messages]
    .reverse()
    .find((message) => message.role === EMessageRole.ASSISTANT);
  return latestAnswer?.isAutoRouted ? latestAnswer : null;
};
